// Base libs
import {
    EvaluationQuestionTypeEnum,
    AssessmentFormQuestionTypeEnum,
    SurveyQuestionTypeEnum,
    EvaluationFormEvaluationFormTypeEnum,
} from "@genesys/cloud-frontend/dist/mjs/models";

// Base Commons
import { RootState } from "app/store";
import FormType from "enums/FormType";
import {
    isNonNegativeNumber,
    isPositiveIntegerWithinMaxLimit,
} from "util/NumberInputOperator";
import { DefaultLimits } from "constants/DefaultLimits";
import AutomatedScoringType from "enums/AutomatedScoringType";

// Feature commons
import { selectCanUserViewSTATopics } from "../auth/currentUserSlice";
import {
    selectUnpublishedTopics,
    selectIsAnyTopicRejected,
} from "features/topics/topicsSlice";

// Feature slices
import { isNotNull } from "features/scorable/scorableSlice";
import {
    selectGroupWeight,
    selectIsFormWeighted,
    selectTopicFilteringFactorFromAssistanceConditions,
    selectAutomatedScoringOption,
    selectIsEvaluationAssistanceConfiguredForQuestion,
    selectQuestionGroupIdContainingQuestion,
    selectIsUserDefinedSelectedForQuestionGroup,
    selectEvaluationFormDefaultAnswers,
    selectLegacyDefaultAnswers,
} from "./qualityFormSlice";
import {
    selectAnswerOption,
    selectAnswerOptionIdList,
    selectAssistanceConditions,
    selectForm,
    selectFormType,
    selectMaxPermissibleQuestionsWithAiScoring,
    selectMaxAiScoringAnswerOptionCount,
    selectEvaluationFormType,
    selectQuestion,
    selectSelectedAutomatedScoringType,
    selectIsAnyDefaultAnswerSelectedForQuestion,
    selectIsAutomatedEvaluationForm,
    selectAutomatedEvaluationFeatureEnabled,
} from "./qualityFormSlices/index";
import {
    isAnswerOptionPointsEmpty,
    isAnswerOptionPointsInDecimalNumber,
    isAnswerOptionPointsOverLimits,
} from "./qualityFormValidations/index";
import { BaseQuestionType } from "./common/BaseQuestionType";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";

export const invalidIntegerInputCharacters = [" ", "+", "-", "e", "."];

// form name/header specific validations

export const selectIsFormNameEmpty = (state: RootState) => {
    const name = state.qualityForm.data.form?.name;
    return name === undefined || name.trim() === "";
};
export const selectIsFormNameLengthy = (state: RootState) => {
    return (
        (state.qualityForm.data.form?.name ?? "").length >
        DefaultLimits.maxFormNameLength
    );
};
export const selectIsFormNameDuplicate = (state: RootState) => {
    const { name, contextId } = selectForm(state) ?? {};
    return state.qualityForm.data.formNames?.some(
        ({ contextId: elementContextId, name: formName }) =>
            formName === name && elementContextId !== contextId
    );
};

export const selectIsPassPercentValid = (state: RootState) => {
    return (
        selectIsPassPercentInLimits(state) &&
        selectIsPassPercentAnInteger(state)
    );
};
export const selectIsPassPercentInLimits = (state: RootState) => {
    const passPercent = state.qualityForm.data.form?.passPercent;
    return (
        passPercent !== undefined &&
        DefaultLimits.minPassPercent <= passPercent &&
        passPercent <= DefaultLimits.maxPassPercent
    );
};
export const selectIsPassPercentAnInteger = (state: RootState) => {
    const passPercent = state.qualityForm.data.form?.passPercent;
    return passPercent !== undefined && (passPercent | 0) === passPercent;
};

// required only for assessments
//TODO : add this selector to isQuestionValid
export const selectIsQuestionCountInvalid = (state: RootState) => {
    if (selectFormType(state) === FormType.ASSESSMENT) {
        return !Object.values(state.qualityForm.data.questions ?? {}).some(
            (question) =>
                question.type != AssessmentFormQuestionTypeEnum.FreeTextQuestion
        );
    }
    return false;
};

// Question Group specific validations
export const selectIsQuestionGroupNameValid = (
    state: RootState,
    questionGroupId: string
) => {
    return (
        selectIsQuestionGroupNamePresent(state, questionGroupId) &&
        !selectIsQuestionGroupNameTooLong(state, questionGroupId)
    );
};
export const selectIsQuestionGroupNamePresent = (
    state: RootState,
    questionGroupId: string
) => {
    return (
        (state.qualityForm.data.questionGroups?.[questionGroupId].name?.trim()
            .length ?? 0) > 0
    );
};
export const selectIsQuestionGroupNameTooLong = (
    state: RootState,
    questionGroupId: string
) => {
    return (
        (state.qualityForm.data.questionGroups?.[questionGroupId].name
            ?.length ?? 0) > DefaultLimits.maxQuestionGroupNameLength
    );
};

export const selectIsQuestionGroupQuestionCountValid = (
    state: RootState,
    questionGroupId: string
) => {
    return (
        (state.qualityForm.data.questionGroups?.[questionGroupId].questions
            ?.length ?? 0) > 0
    );
};

export const selectIsQuestionGroupScalingFactorValid = (
    state: RootState,
    questionGroupId: string
) => {
    const weight = selectGroupWeight(state, questionGroupId);
    return (
        selectIsScalingFactorPresent(weight) &&
        selectIsScalingFactorUnderMaxLimit(weight!)
    );
};

export const selectIsScalingFactorPresent = (weight?: number) => {
    return weight !== undefined && isNonNegativeNumber(weight);
};
export const selectIsScalingFactorUnderMaxLimit = (weight: number) => {
    return weight <= DefaultLimits.maxQuestionGroupScalingFactor;
};

export const selectIsAnAnswersDefaultToSelected = (
    state: RootState,
    questionGroupId: string
) => {
    const isDefaultAnsweringEnabled = selectFeatureToggle(
        state,
        FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
    );

    if (isDefaultAnsweringEnabled) {
        const answerDefaultTo = selectEvaluationFormDefaultAnswers(
            state,
            questionGroupId
        );
        return (
            answerDefaultTo?.highestScore ||
            answerDefaultTo?.lowestScore ||
            answerDefaultTo?.userDefined ||
            answerDefaultTo?.notApplicable
        );
    }
    const answerDefaultTo = selectLegacyDefaultAnswers(state, questionGroupId);
    return (
        answerDefaultTo?.defaultAnswersToHighest ||
        answerDefaultTo?.defaultAnswersToNA
    );
};

export const selectIsQuestionGroupValid = (
    state: RootState,
    questionGroupId: string
) => {
    const isAutomatedEvaluationForm =
        selectEvaluationFormType(state) ===
        EvaluationFormEvaluationFormTypeEnum.Automated;

    return (
        selectIsQuestionGroupNameValid(state, questionGroupId) &&
        selectIsQuestionGroupQuestionCountValid(state, questionGroupId) &&
        (!selectIsFormWeighted(state) ||
            selectIsQuestionGroupScalingFactorValid(state, questionGroupId)) &&
        (!isAutomatedEvaluationForm ||
            selectIsAnAnswersDefaultToSelected(state, questionGroupId))
    );
};

// Question & answer specific validations

export const selectIsQuestionNameValid = (
    state: RootState,
    questionId: string
) => {
    return (
        selectIsQuestionNamePresent(state, questionId) &&
        !selectIsQuestionNameTooLengthy(state, questionId)
    );
};
export const selectIsQuestionNamePresent = (
    state: RootState,
    questionId: string
) => {
    return (
        (state.qualityForm.data.questions?.[questionId].text?.trim().length ??
            0) > 0
    );
};
export const selectIsQuestionNameTooLengthy = (
    state: RootState,
    questionId: string
) => {
    return (
        (state.qualityForm.data.questions?.[questionId].text?.length ?? 0) >
        DefaultLimits.maxQuestionNameLength
    );
};

// NOTE: for Evaluation Form, we didn't add this validation to UI because there is no implementation for limit in the backend
export const selectIsQuestionHelpTextValid = (
    state: RootState,
    questionId: string
) => {
    const formType = selectFormType(state);
    return formType === FormType.ASSESSMENT || formType === FormType.SURVEY
        ? (state.qualityForm.data.questions?.[questionId].helpText ?? "")
              .length <= DefaultLimits.maxHelpTextLength
        : true;
};
export const selectIsAnswerOptionTextPresent = (
    state: RootState,
    answerOptionId: string,
    isMultipleSelect?: boolean
) => {
    const answerOption = selectAnswerOption(
        state,
        answerOptionId,
        isMultipleSelect
    );
    return (answerOption?.text?.trim().length ?? 0) > 0;
};
export const selectIsAnswerOptionTextUnderMaxLength = (
    state: RootState,
    answerOptionId: string,
    isMultipleSelect?: boolean
) => {
    const answerOption = selectAnswerOption(
        state,
        answerOptionId,
        isMultipleSelect
    );
    return (
        (answerOption?.text?.length ?? 0) <= DefaultLimits.maxAnswerTextLength
    );
};
export const selectIsAiScoringAnswerOptionCountValid = (
    state: RootState,
    questionId: string
) => {
    const maxAiScoringAnswerOptionCount =
        selectMaxAiScoringAnswerOptionCount(state);

    return (
        selectAutomatedScoringOption(state, questionId) !==
            AutomatedScoringType.AI_SCORING ||
        (state.qualityForm.data.questions?.[questionId].answerOptions?.length ??
            0) <= maxAiScoringAnswerOptionCount
    );
};

export const selectMaxAllowedResponseCharacters = (
    state: RootState,
    questionId?: string
) => {
    const isSurvey = selectFormType(state) === FormType.SURVEY;
    const isFreeText =
        questionId != null &&
        state.qualityForm.data.questions?.[questionId]?.type ===
            SurveyQuestionTypeEnum.FreeTextQuestion;
    const isSurveyFreeTextLimitIncreaseFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.VOICE_SURVEY_FREE_TEXT_CHAR_LIMIT_INCREASE
    );
    return isSurvey && isFreeText && isSurveyFreeTextLimitIncreaseFTEnabled
        ? DefaultLimits.maxResponseCharactersSurveyFreeText
        : DefaultLimits.maxResponseCharacters;
};

export const selectIsQuestionHavingValidMaxResponseChar = (
    state: RootState,
    questionId: string
) => {
    const question = state.qualityForm.data.questions?.[questionId];
    if (
        question &&
        (question.type === SurveyQuestionTypeEnum.FreeTextQuestion ||
            question.type === AssessmentFormQuestionTypeEnum.FreeTextQuestion ||
            question.type === SurveyQuestionTypeEnum.NpsQuestion)
    ) {
        const maxChars = question.maxResponseCharacters;
        return isPositiveIntegerWithinMaxLimit(
            maxChars,
            selectMaxAllowedResponseCharacters(state, questionId)
        );
    }
    return true;
};
export const selectIsFollowUpExplanationValid = (
    state: RootState,
    questionId: string
) => {
    const question = state.qualityForm.data.questions?.[questionId];
    if (
        question &&
        question.type === SurveyQuestionTypeEnum.NpsQuestion &&
        question.explanationPrompt !== undefined
    ) {
        return (
            question.explanationPrompt !== "" &&
            !selectIsFollowUpExplanationTooLengthy(state, questionId)
        );
    }
    return true;
};

export const selectIsFollowUpExplanationTooLengthy = (
    state: RootState,
    questionId: string
) => {
    return (
        (state.qualityForm.data.questions?.[questionId].explanationPrompt ?? "")
            .length > DefaultLimits.maxExplanationPromptLength
    );
};

export const isAnswerOptionPointsValid = (
    state: RootState,
    answerOptionId: string,
    isMultipleSelect?: boolean
) => {
    // With multiple selection support, each check for multiple selection answer option
    // is a little more expensive.  Structure below would allow transpiler to stop
    // when point is empty and the rest does not get called.
    const hasInvalidFactor =
        isAnswerOptionPointsEmpty(state, answerOptionId, isMultipleSelect) ||
        isAnswerOptionPointsOverLimits(
            state,
            answerOptionId,
            isMultipleSelect
        ) ||
        isAnswerOptionPointsInDecimalNumber(
            state,
            answerOptionId,
            isMultipleSelect
        );
    return !hasInvalidFactor;
};

export const selectIsAssistanceConditionsHavingTooManyTopics = (
    state: RootState,
    answerId: string,
    isMultipleSelect?: boolean
) => {
    const aConditions = selectAssistanceConditions(
        state,
        answerId,
        isMultipleSelect
    );
    return (
        (aConditions?.flatMap((condition) => condition.topicIds).length ?? 0) >
        DefaultLimits.maxAssistanceConditionTopics
    );
};
export const selectIsAssistanceConditionsHavingInsufficientTopics = (
    state: RootState,
    answerId: string,
    isMultipleSelect?: boolean
) => {
    const aConditions = selectAssistanceConditions(
        state,
        answerId,
        isMultipleSelect
    );
    return (aConditions ?? []).some(
        (condition) => (condition.topicIds ?? []).length === 0
    );
};

export const selectHasSameTopicInDifferentConditions = (
    state: RootState,
    answerId: string,
    isMultipleSelect?: boolean
) => {
    return selectTopicFilteringFactorFromAssistanceConditions(
        state,
        answerId,
        isMultipleSelect
    ).hasSameTopicInDifferentConditions;
};

export const selectIsAnyTopicInAssistanceConditionInvalid = (
    state: RootState,
    answerId: string,
    isMultipleSelect?: boolean
) => {
    const topicIds = selectAssistanceConditions(
        state,
        answerId,
        isMultipleSelect
    )
        ?.flatMap((condition) => condition.topicIds)
        .filter(isNotNull);

    const skipUnpublishedTopicCheck = !selectCanUserViewSTATopics(state);
    if (topicIds) {
        const anyUnpublishedTopic = skipUnpublishedTopicCheck
            ? false
            : (selectUnpublishedTopics(state, topicIds)?.length ?? 0) > 0;
        const anyRejectedTopic = selectIsAnyTopicRejected(state, topicIds);
        return anyUnpublishedTopic || anyRejectedTopic;
    }
    return false;
};

export const selectIsAnswerHavingValidAssistedConditions = (
    state: RootState,
    answerId: string,
    isMultipleSelect?: boolean
) => {
    const answerOption = selectAnswerOption(state, answerId, isMultipleSelect);
    const conditions = answerOption?.assistanceConditions;
    if (!conditions) {
        return true;
    }

    return (
        !selectIsAssistanceConditionsHavingTooManyTopics(
            state,
            answerId,
            isMultipleSelect
        ) &&
        !selectIsAssistanceConditionsHavingInsufficientTopics(
            state,
            answerId,
            isMultipleSelect
        ) &&
        !selectIsAnyTopicInAssistanceConditionInvalid(
            state,
            answerId,
            isMultipleSelect
        ) &&
        !selectHasSameTopicInDifferentConditions(
            state,
            answerId,
            isMultipleSelect
        )
    );
};

export const selectIsAnswerOptionValid = (
    state: RootState,
    answerOptionId: string,
    isMultipleSelect?: boolean
) => {
    return (
        selectIsAnswerOptionTextPresent(
            state,
            answerOptionId,
            isMultipleSelect
        ) &&
        selectIsAnswerOptionTextUnderMaxLength(
            state,
            answerOptionId,
            isMultipleSelect
        ) &&
        isAnswerOptionPointsValid(state, answerOptionId, isMultipleSelect) &&
        selectIsAnswerHavingValidAssistedConditions(
            state,
            answerOptionId,
            isMultipleSelect
        )
    );
};

export const selectIsQuestionHavingValidAnswers = (
    state: RootState,
    questionId: string
) => {
    const question = state.qualityForm.data.questions?.[questionId];
    if (
        question?.type === EvaluationQuestionTypeEnum.MultipleChoiceQuestion ||
        question?.type ===
            AssessmentFormQuestionTypeEnum.MultipleChoiceQuestion ||
        question?.type === SurveyQuestionTypeEnum.MultipleChoiceQuestion
    ) {
        return question.answerOptions?.every((id) =>
            selectIsAnswerOptionValid(state, id)
        );
    } else if (
        question?.type === EvaluationQuestionTypeEnum.MultipleSelectQuestion
    ) {
        return question.multipleSelectOptionQuestions?.every((id) =>
            selectIsAnswerOptionValid(state, id, true)
        );
    }
    return true;
};

export const selectIsQuestionAnswerAutomated = (
    state: RootState,
    questionId: string
) => {
    const questionAutomatedScoringType = selectSelectedAutomatedScoringType(
        state,
        questionId
    );

    return (
        selectEvaluationFormType(state) !==
            EvaluationFormEvaluationFormTypeEnum.Automated ||
        questionAutomatedScoringType === AutomatedScoringType.AI_SCORING ||
        questionAutomatedScoringType ===
            AutomatedScoringType.EVALUATION_ASSISTANCE
    );
};

export const selectIsQuestionValidForEA = (
    state: RootState,
    questionId: string
): boolean => {
    const question = selectQuestion(state, questionId);
    const isEASelected =
        question?.automatedScoringType ===
        AutomatedScoringType.EVALUATION_ASSISTANCE;
    if (!isEASelected) {
        return true;
    }
    const isEAConfigured = selectIsEvaluationAssistanceConfiguredForQuestion(
        state,
        questionId
    );
    return isEAConfigured;
};

export const selectIsQuestionValidForUserDefinedDefaultAnswer = (
    state: RootState,
    questionId: string
) => {
    const isDefaultAnsweringEnabled = selectFeatureToggle(
        state,
        FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
    );
    const questionGroupId =
        selectQuestionGroupIdContainingQuestion(questionId)(
            state.qualityForm
        ) ?? "";
    const isUserDefinedSelected = selectIsUserDefinedSelectedForQuestionGroup(
        state,
        questionGroupId
    );
    const isAutomatedEvaluationForm =
        selectEvaluationFormType(state) ===
        EvaluationFormEvaluationFormTypeEnum.Automated;

    return (
        !isDefaultAnsweringEnabled ||
        !isAutomatedEvaluationForm ||
        !isUserDefinedSelected ||
        selectIsAnyDefaultAnswerSelectedForQuestion(state, questionId)
    );
};

export const selectIsQuestionValidForNADefaultAnswer = (
    state: RootState,
    questionId: string
) => {
    const isAutomatedEvaluationCompletionEnabled =
        selectAutomatedEvaluationFeatureEnabled(state);

    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);

    const questionGroupId =
        selectQuestionGroupIdContainingQuestion(questionId)(
            state.qualityForm
        ) ?? "";

    const isQuestionNAEnabled = selectQuestion(state, questionId)?.naEnabled;

    if (isAutomatedEvaluationCompletionEnabled && isAutomatedEvaluationForm) {
        const answerDefaultTo = selectEvaluationFormDefaultAnswers(
            state,
            questionGroupId
        );

        const isOnlyNADefaultEnabled =
            answerDefaultTo?.notApplicable &&
            !answerDefaultTo?.highestScore &&
            !answerDefaultTo?.lowestScore &&
            !answerDefaultTo?.userDefined;

        // If N/A is the only default answer enabled at group level, the question must have N/A enabled
        if (isOnlyNADefaultEnabled) {
            return isQuestionNAEnabled;
        }
    }
    return true;
};

export const selectIsQuestionValid = (state: RootState, questionId: string) => {
    return (
        selectIsQuestionNameValid(state, questionId) &&
        selectIsQuestionHavingValidAnswers(state, questionId) &&
        selectIsQuestionHavingValidMaxResponseChar(state, questionId) &&
        selectIsFollowUpExplanationValid(state, questionId) &&
        selectIsQuestionHelpTextValid(state, questionId) &&
        selectIsAiScoringAnswerOptionCountValid(state, questionId) &&
        selectIsQuestionAnswerAutomated(state, questionId) &&
        selectIsQuestionValidForEA(state, questionId) &&
        selectIsQuestionValidForUserDefinedDefaultAnswer(state, questionId) &&
        selectIsQuestionValidForNADefaultAnswer(state, questionId)
    );
};

export const hasMaximumCriticalAnswerOptions = (
    state: RootState,
    questionId: string,
    isMultipleSelect?: boolean
) => {
    // Keep it simple for multiple select as it only has Selected/Unselected
    if (isMultipleSelect) {
        return false;
    }
    const answerOptionIdList = selectAnswerOptionIdList(state, questionId);
    return (
        (answerOptionIdList?.length ?? 0) >
        DefaultLimits.maxCriticalAnswerOptionCount
    );
};

export const hasMaximumAIScoringAnswerOptions = (
    state: RootState,
    questionId: string
) => {
    const maxAiScoringAnswerOptionCount =
        selectMaxAiScoringAnswerOptionCount(state);
    return (
        (state.qualityForm.data.questions?.[questionId].answerOptions?.length ??
            0) >= maxAiScoringAnswerOptionCount
    );
};

export const selectIsAIScoringQuestionCountValid = (state: RootState) => {
    const maxLimit = selectMaxPermissibleQuestionsWithAiScoring(state);

    return (
        Object.values(
            state.qualityForm.data.aiScoring?.questionGroupSettings || {}
        )
            .flatMap((group) => Object.values(group.questionSettings))
            .filter((question) => question?.enabled).length <= maxLimit
    );
};

export const selectFormHasValidAIScoringQuestions = (state: RootState) => {
    const maxAiScoringAnswerOptionCount =
        selectMaxAiScoringAnswerOptionCount(state);
    Object.values(state.qualityForm.data.questions ?? {}).forEach((element) => {
        if (
            selectAutomatedScoringOption(state, element.id ?? "") ==
            AutomatedScoringType.AI_SCORING
        ) {
            if (element.answerOptions.length > maxAiScoringAnswerOptionCount) {
                return false;
            }
        }
    });

    return true;
};

export const selectIsFormValid = (state: RootState) => {
    // skips name validation for assessment forms
    if (
        state.qualityForm.data.formType !== FormType.ASSESSMENT &&
        (selectIsFormNameDuplicate(state) ||
            selectIsFormNameEmpty(state) ||
            selectIsFormNameLengthy(state))
    ) {
        return false;
    }
    if (
        state.qualityForm.data.formType === FormType.ASSESSMENT &&
        (selectIsQuestionCountInvalid(state) ||
            !selectIsPassPercentValid(state))
    ) {
        return false;
    }
    //Basic Check : checking if we have atleast one question group
    const isQuestionGroupPresent =
        (state.qualityForm.data.form?.questionGroups ?? []).length > 0;
    if (!isQuestionGroupPresent) {
        return false;
    }

    // Checking question group name to be not empty  + Questions > 0 in each question group.
    const isFormHavingValidQuestionGroups =
        state.qualityForm.data.form?.questionGroups.every((questionGroupId) =>
            selectIsQuestionGroupValid(state, questionGroupId)
        );
    if (!isFormHavingValidQuestionGroups) {
        return false;
    }

    const isFormHavingValidQuestions = Object.keys(
        state.qualityForm.data.questions ?? {}
    ).every((id) => selectIsQuestionValid(state, id));

    if (
        !selectFormHasValidAIScoringQuestions(state) ||
        !selectIsAIScoringQuestionCountValid(state)
    ) {
        return false;
    }

    if (!isFormHavingValidQuestions || !selectIsQuestionCountValid(state)) {
        return false;
    }

    return true;
};

export const selectFormHasMaxAIScoringQuestions = (state: RootState) => {
    const questionGroups =
        state.qualityForm.data.aiScoring?.questionGroupSettings || {};

    const maxLimit = selectMaxPermissibleQuestionsWithAiScoring(state);

    let count = 0;

    Object.values(questionGroups).forEach((group) => {
        Object.values(group.questionSettings).forEach((question) => {
            if (question?.enabled) {
                count++;
            }
        });
    });

    return count >= maxLimit;
};

export const selectIsQuestionCountValid = (state: RootState) => {
    return (
        Object.keys(state.qualityForm.data.questions ?? {}).length <=
        DefaultLimits.maxQuestionCount
    );
};

export const selectIsQuestionCountAtOrAboveLimit = (state: RootState) => {
    return (
        Object.keys(state.qualityForm.data.questions ?? {}).length >=
        DefaultLimits.maxQuestionCount
    );
};

export const selectIsNpsQuestionInvalid =
    (questions: string[]) => (state: RootState) => {
        return questions.some((questionId) => {
            const question = selectQuestion(state, questionId);
            return (
                question &&
                question.type === SurveyQuestionTypeEnum.NpsQuestion &&
                question.explanationPrompt?.trim() === ""
            );
        });
    };

export const selectInvalidTopicsInAssistanceCondition = (
    state: RootState,
    answerId: string,
    index: number,
    isMultipleSelect?: boolean
) => {
    const topicIds = selectAssistanceConditions(
        state,
        answerId,
        isMultipleSelect
    )?.[index].topicIds;
    if (topicIds) {
        return selectUnpublishedTopics(state, topicIds);
    }
    return [];
};

export const isPreviewToggleDisabled = (state: RootState) => {
    return !selectIsFormValid(state);
};

export const selectIsQuestionHavingValidAssistedConditions = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    if (question?.type === BaseQuestionType.MultipleChoiceQuestion) {
        // if question is MCQ
        // iterate answerOption item and call selectIsAnswerHavingValidAssistedConditions
        // if it returns false, then return false
        return question.answerOptions?.every((answerOptionId) => {
            return selectIsAnswerHavingValidAssistedConditions(
                state,
                answerOptionId,
                false
            );
        });
    } else if (question?.type === BaseQuestionType.MultipleSelectQuestion) {
        // if question is MSQ
        // iterate its multipleSelectQuestionOptions item
        return question.multipleSelectOptionQuestions?.every(
            (answerOptionId) => {
                return selectIsAnswerHavingValidAssistedConditions(
                    state,
                    answerOptionId,
                    true
                );
            }
        );
    } else {
        return true;
    }
};

export const selectHasMultipleSelectQuestions = (state: RootState): boolean => {
    const questions = state.qualityForm.data.questions || {};

    return Object.keys(questions).some(
        (questionId) =>
            questions[questionId].type ===
            EvaluationQuestionTypeEnum.MultipleSelectQuestion
    );
};

// export const selectIsAnswerHavingValidAiScoringConfiguration = (
//     state: RootState,
//     answerId: string
// ) => {
//     // TODO: update selector function with AiScoring logic to validate answer
//     // from given answerId
//     return true;
// };
